import { Button } from '@/components/ui/button';

import { BetweenHorizonalStart } from 'lucide-react';
import { useRouter } from 'next/navigation';
import React from 'react';
import { updateCartCheckout } from '@/lib/actions/checkout/action';
import { toast } from '@/components/ui/use-toast';
import LoadingAnimationIcon from '@/components/icons/loading-animation-icon';
import { ElementProps } from '@/components/sections/builder/types';
import { useBuilderStore } from '@/stores/builder';
import { shallow } from 'zustand/shallow';
import { HttpTypes } from '@/types';

const defaultData: { name: string } = {
  name: 'Checkout button',
};

export const ElementContent = ({ element, preview }: ElementProps) => {
  const router = useRouter();

  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const builder = useBuilderStore((state) => state, shallow);
  const themeContent = preview ? builder.theme : element.theme;
  const cart = element.parentData as HttpTypes.StoreCart;

  const disableButton = cart?.item_stock?.some((item) => item.stock === 0);

  const handleToCheckout = async () => {
    try {
      setIsLoading(true);

      const host = typeof window !== 'undefined' ? window.location.host : '';
      const result = await updateCartCheckout(host as string);

      if (result.success) {
        return router.push(`/checkout/${result.data.checkout_id}`);
      }

      if (!cart.items?.length) {
        return toast({
          title: 'Error',
          description: 'Cart is empty',
          variant: 'destructive',
        });
      }

      if (result.status === 404) {
        await fetch('/api/remove-cart-cookie', { method: 'GET' });

        toast({
          title: 'Error',
          description: result.message,
          variant: 'destructive',
        });

        return (window.location.href = '/');
      }

      if (result.status === 400) {
        toast({
          title: 'Error',
          description: result.message,
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Something went wrong',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      className='theme_button theme_cart_page_btn flex w-full items-center px-24 md:w-fit'
      disabled={isLoading || disableButton}
      onClick={handleToCheckout}
    >
      {isLoading && <LoadingAnimationIcon />}
      <span className='mx-2'>{themeContent?.cart?.labelCheckout || 'Checkout'}</span>
    </Button>
  );
};

export const ElementPreviewIcon = BetweenHorizonalStart;

export function ElementSetting() {
  return <div></div>;
}

const CartCheckoutButton = {
  type: 'cart_checkout_button',
  group: 'cart_total',
  defaultData,
  Element: ElementContent,
  Setting: ElementSetting,
  PreviewIcon: ElementPreviewIcon,
  Icon: ElementPreviewIcon,
  childs: [],
  maxEntries: 1,
};

export default CartCheckoutButton;
